import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Trans, useTranslation } from "react-i18next";
import { useGetInvitePreviewQuery } from "components/Slices/circleApiSlice";
import { ImageAvatar } from "components/Circle/shared/ImageAvatar";

// "every 2 weeks" / "every week" / "daily" — invitee-facing cadence label.
// Routed through t() for the unit noun too: English pluralizes with a
// template-level "s" (cadenceEveryCountUnit), Somali's template needs none
// ("2 toddobaad kasta"), so each language's JSON owns its own grammar.
const useCadenceLabel = () => {
  const { t } = useTranslation();
  const unitKey = { day: "join.unitDay", week: "join.unitWeek", month: "join.unitMonth" } as const;
  return (unit: "day" | "week" | "month", count: number) => {
    if (count === 1) {
      return unit === "day"
        ? t("join.cadenceDaily")
        : t("join.cadenceEveryUnit", { unit: t(unitKey[unit]) });
    }
    return t("join.cadenceEveryCountUnit", { count, unit: t(unitKey[unit]) });
  };
};

// Public landing page for shareable invite links (sanduuq.com/join/<token>).
// Works logged-out: this is the first thing a not-yet-user sees after tapping
// a WhatsApp invite, so it must explain the circle before asking for signup.
const JoinCirclePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cadenceLabel = useCadenceLabel();
  const { token } = useParams<{ token: string }>();

  const { data, isLoading, isError } = useGetInvitePreviewQuery(
    { token: token || "" },
    { skip: !token }
  );

  const card = {
    width: "100%",
    maxWidth: 440,
    borderRadius: 4,
    bgcolor: "background.paper",
    boxShadow: "0 8px 32px rgba(16,24,64,0.14), 0 2px 8px rgba(16,24,64,0.08)",
    overflow: "hidden",
  };

  const frame = (content: React.ReactNode) => (
    <Box
      sx={{
        minHeight: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 6,
      }}
    >
      {content}
    </Box>
  );

  if (isLoading) {
    return frame(<CircularProgress />);
  }

  if (isError || !data) {
    return frame(
      <Box sx={{ ...card, p: 4, textAlign: "center" }}>
        <LinkOffIcon sx={{ fontSize: "2.4rem", color: "text.disabled", mb: 1.5 }} />
        <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, mb: 0.75 }}>
          {t("join.deadTitle")}
        </Typography>
        <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", mb: 3 }}>
          {t("join.deadBody")}
        </Typography>
        <Button
          variant="contained"
          fullWidth
          onClick={() => navigate("/home")}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", py: 1.1 }}
        >
          {t("join.explore")}
        </Button>
      </Box>
    );
  }

  const { circle, inviter, invitee } = data;
  const amount = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: circle.currency || "CAD",
    maximumFractionDigits: 0,
  }).format(circle.contribution);
  const cadence = cadenceLabel(circle.intervalUnit, circle.intervalCount);

  const spots =
    circle.maxMembers != null
      ? t("join.spotsFilled", { accepted: circle.acceptedCount, max: circle.maxMembers })
      : t("join.membersSoFar", { count: circle.acceptedCount });

  return frame(
    <Box sx={card}>
      <Box sx={{ height: 5, bgcolor: "primary.main" }} />
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        {/* Inviter */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
          <ImageAvatar
            displayImg={inviter?.displayImg ?? undefined}
            firstName={inviter?.name || "A"}
            sx={{ width: 44, height: 44, fontSize: "1rem" }}
          />
          <Box>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, lineHeight: 1.3 }}>
              {t("join.invitedYou", { name: inviter?.name || t("join.someone") })}
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
              {t("join.toJoin")}
            </Typography>
          </Box>
        </Box>

        {/* Circle */}
        <Typography sx={{ fontSize: "1.45rem", fontWeight: 800, lineHeight: 1.2, mb: 2 }}>
          {circle.title}
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            mb: 3,
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 0.6,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "primary.main" }}>
              {amount} {cadence}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 1.5,
              py: 0.6,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.text.secondary, 0.07),
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <GroupsIcon sx={{ fontSize: "0.95rem", color: "text.secondary" }} />
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "text.secondary" }}>
              {spots}
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", mb: 3, lineHeight: 1.55 }}>
          {t("join.explainer", { amount, cadence })}
        </Typography>

        <Divider sx={{ mb: 2.5 }} />

        {/* Invited-email note */}
        {invitee.emailHint && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2.5,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
            }}
          >
            <EmailOutlinedIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
            <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
              <Trans
                i18nKey="join.inviteForEmail"
                values={{
                  email: invitee.emailHint,
                  instruction: invitee.hasAccount
                    ? t("join.instructionHasAccount")
                    : t("join.instructionNoAccount"),
                }}
                components={{ bold: <b /> }}
              />
            </Typography>
          </Box>
        )}

        <Button
          variant="contained"
          fullWidth
          onClick={() => navigate(invitee.hasAccount ? "/login" : "/register")}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.95rem",
            borderRadius: "10px",
            py: 1.25,
          }}
        >
          {invitee.hasAccount ? t("join.logInToAccept") : t("join.createYourAccount")}
        </Button>

        <Button
          fullWidth
          onClick={() => navigate(invitee.hasAccount ? "/register" : "/login")}
          sx={{
            mt: 1,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8rem",
            color: "text.secondary",
          }}
        >
          {invitee.hasAccount ? t("join.newHereCreateAccount") : t("join.alreadyHaveAccount")}
        </Button>
      </Box>
    </Box>
  );
};

export default JoinCirclePage;
